import { ComponentProps, forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';

import { computePopoverStyle } from '../lib';
import { PopoverAnchorRef, PopoverOrigin } from '../model/types';
import { styles } from './styles';

export interface PopoverProps extends ComponentProps<'div'> {
  anchorRef: PopoverAnchorRef;
  anchorOrigin: PopoverOrigin;
  transformOrigin: PopoverOrigin;
  open?: boolean;
  onClose?: () => void;
  preventOverflow?: boolean;
  classNamePosition?: string;
}

export const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  ({ anchorRef, anchorOrigin, transformOrigin, open = true, onClose, preventOverflow = false, ...props }, ref) => {
    const popoverRef = useRef<HTMLDivElement>(null);
    const [internOpen, setInternOpen] = useState(false);
    const { className, ...cleanProps } = props;

    useImperativeHandle(ref, () => popoverRef.current! as HTMLDivElement);

    useEffect(() => {
      if (open) {
        popoverRef.current?.showPopover();
      }
    }, [open]);

    const updateGeometry = () => {
      const popover = popoverRef.current;

      if (!popover) {
        return;
      }

      const style = computePopoverStyle(anchorRef, popoverRef, anchorOrigin, transformOrigin, preventOverflow);
      Object.assign(popover.style, style);
    };

    const handleWindowClick = (event: MouseEvent) => {
      if (popoverRef.current?.contains(event.target as HTMLElement)) {
        return;
      }

      onClose?.();
    };

    useLayoutEffect(() => {
      const popover = popoverRef.current;

      if (!popover || !open) {
        return;
      }

      setInternOpen(true);
      const resizeObserver = new ResizeObserver(updateGeometry);
      resizeObserver.observe(popover);
      window.addEventListener('scroll', updateGeometry, true);
      window.addEventListener('resize', updateGeometry);

      const timeoutId = window.setTimeout(() => {
        window.addEventListener('click', handleWindowClick);
      }, 0);
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('scroll', updateGeometry, true);
        window.removeEventListener('resize', updateGeometry);
        window.clearTimeout(timeoutId);
        window.removeEventListener('click', handleWindowClick);
      };
    }, [
      open,
      anchorRef,
      anchorOrigin.horizontal,
      anchorOrigin.vertical,
      transformOrigin.horizontal,
      transformOrigin.vertical,
      preventOverflow,
    ]);

    const handleAnimationEnd = () => {
      if (open) {
        return;
      }

      setInternOpen(false);
    };

    if (!open && !internOpen) {
      return null;
    }

    const popoverClassName = [
      className,
      stylex.props(styles.popover, internOpen && styles.opening, !open && styles.closing).className,
    ].join(' ');

    return (
      <div
        ref={popoverRef}
        onAnimationEnd={handleAnimationEnd}
        {...cleanProps}
        className={popoverClassName}
        {...{ popover: 'auto' }}
      />
    );
  }
);
